import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { useAgent } from "@/lib/agentContext";
import api from "@/lib/api";
import {
  ArrowRight, MessageCircle, ShieldCheck, BadgeCheck, Building2,
  Home as HomeIcon, MapPin, IndianRupee, CalendarClock, Loader2,
} from "lucide-react";

const BHK_VALUES = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "4+ BHK"];
const PROPERTY_TYPES = ["Flat", "Villa", "Row House", "Office", "Shop"];
const RENT_BANDS = [
  { label: "Any", value: "" },
  { label: "Under ₹25 k", value: "0-25000" },
  { label: "₹25 k – ₹50 k", value: "25000-50000" },
  { label: "₹50 k – ₹1 L", value: "50000-100000" },
  { label: "₹1 L +", value: "100000-" },
];

// Relative /api/... paths from rental.images[] resolve to the same origin
// (kubernetes ingress routes /api/* to backend). For dev/preview cross-origin
// setups, prefix with REACT_APP_BACKEND_URL explicitly.
const resolveImg = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url}`;
};

function RentalCard({ rental, detailHref }) {
  const img = (rental.images || [])[0];
  return (
    <Link
      to={detailHref}
      className="sg-card overflow-hidden flex flex-col hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
      data-testid={`rental-card-${rental.rental_id}`}
    >
      <div className="aspect-[16/10] bg-sg-cream-soft relative overflow-hidden">
        {img ? (
          <img src={resolveImg(img)} alt={rental.address} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sg-mute">
            <HomeIcon className="w-10 h-10" />
          </div>
        )}
        <div className="absolute top-3 left-3 chip chip-gold text-[10px]">RENTAL</div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-display text-xl text-sg-navy leading-tight">
            {rental.bhk} · {rental.property_type}
          </div>
          <div className="font-mono text-sm text-sg-gold-deep whitespace-nowrap">
            <IndianRupee className="w-3 h-3 inline -mt-0.5" />
            {Number(rental.monthly_rent).toLocaleString("en-IN")}/mo
          </div>
        </div>
        <div className="text-xs text-sg-mute mt-1 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {rental.locality} · {rental.address}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
          <span className="chip">{rental.furnishing}</span>
          {rental.area_sqft ? <span className="chip">{rental.area_sqft} sqft</span> : null}
          {rental.floor_number ? <span className="chip">Floor {rental.floor_number}{rental.total_floors ? `/${rental.total_floors}` : ""}</span> : null}
        </div>
        {rental.description ? (
          <p className="text-sm text-sg-mute mt-3 line-clamp-2">{rental.description}</p>
        ) : null}
        <div className="mt-auto pt-4 flex items-center justify-between border-t border-sg-line/70">
          <div className="text-xs text-sg-mute inline-flex items-center gap-1">
            <CalendarClock className="w-3 h-3" /> Available {rental.available_from}
          </div>
          <span
            className="text-xs font-semibold text-sg-navy group-hover:text-sg-gold-deep inline-flex items-center gap-1"
            data-testid={`rental-view-btn-${rental.rental_id}`}
          >
            View details <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Rent() {
  const agent = useAgent();
  const [searchParams] = useSearchParams();

  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localities, setLocalities] = useState({ "Pune West": [], "Pune East": [] });

  // filters (seeded from URL params)
  const [locality, setLocality] = useState(searchParams.get("locality") || "");
  const [bhk, setBhk] = useState(searchParams.get("bhk") || "");
  const [propertyType, setPropertyType] = useState("");
  const [rentBand, setRentBand] = useState("");

  const detailBase = agent ? `/agent/${agent.slug}/rental` : "/rental";

  const allLocalities = useMemo(
    () => [...(localities["Pune West"] || []), ...(localities["Pune East"] || [])],
    [localities],
  );

  useEffect(() => {
    api.get("/localities").then(({ data }) => setLocalities(data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (locality) params.locality = locality;
    if (bhk) params.bhk = bhk;
    if (propertyType) params.property_type = propertyType;
    if (rentBand) {
      const [mn, mx] = rentBand.split("-");
      if (mn) params.min_rent = mn;
      if (mx) params.max_rent = mx;
    }
    api.get("/rentals", { params })
      .then(({ data }) => setRentals(data.rentals || []))
      .catch(() => setRentals([]))
      .finally(() => setLoading(false));
  }, [locality, bhk, propertyType, rentBand]);

  // Filter to a specific agent's listings when viewing under /agent/:slug/rent
  const visibleRentals = useMemo(() => {
    if (agent?.slug) return rentals.filter((r) => r.agent_slug === agent.slug);
    return rentals;
  }, [rentals, agent]);

  const waNum = agent?.phone ? agent.phone.replace(/[^0-9]/g, "") : "919999999999";
  const waMsg = agent
    ? `Hi ${agent.name}, I'm looking for a rental in ${agent.primary_locality || "Pune"}.`
    : "Hi, I'm looking for a rental in Pune.";
  const waHref = `https://wa.me/${waNum}?text=${encodeURIComponent(waMsg)}`;

  const inner = (
    <>
      {/* HERO */}
      <section className="bg-sg-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20"
             style={{ backgroundImage: "url(https://images.pexels.com/photos/27826399/pexels-photo-27826399.jpeg)" }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="overline text-sg-gold mb-4">RENT · {agent?.primary_locality?.toUpperCase() || "PUNE"}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            {agent
              ? <>Verified rentals from <span className="font-serif-italic text-sg-gold">{agent.name.split(" ")[0]}.</span></>
              : <>Verified rentals in <span className="font-serif-italic text-sg-gold">Pune.</span></>}
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-base">
            Every listing is posted by a MahaCRITI-registered agent — no portal spam, no cold callers.
            Filter by locality, BHK and budget, then request a visit in one tap.
          </p>

          {/* Filter bar */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-2 max-w-4xl">
            <select value={locality} onChange={(e) => setLocality(e.target.value)}
                    data-testid="rent-filter-locality"
                    className="h-11 rounded-xl border border-white/25 bg-white/10 backdrop-blur-2xl text-white text-sm px-3 focus:outline-none focus:border-sg-gold appearance-none">
              <option value="" className="bg-sg-navy">Any locality</option>
              {allLocalities.map((l) => (
                <option key={l} value={l} className="bg-sg-navy">{l}</option>
              ))}
            </select>
            <select value={bhk} onChange={(e) => setBhk(e.target.value)}
                    data-testid="rent-filter-bhk"
                    className="h-11 rounded-xl border border-white/25 bg-white/10 backdrop-blur-2xl text-white text-sm px-3 focus:outline-none focus:border-sg-gold appearance-none">
              <option value="" className="bg-sg-navy">Any BHK</option>
              {BHK_VALUES.map((b) => <option key={b} value={b} className="bg-sg-navy">{b}</option>)}
            </select>
            <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}
                    data-testid="rent-filter-type"
                    className="h-11 rounded-xl border border-white/25 bg-white/10 backdrop-blur-2xl text-white text-sm px-3 focus:outline-none focus:border-sg-gold appearance-none">
              <option value="" className="bg-sg-navy">Any type</option>
              {PROPERTY_TYPES.map((t) => <option key={t} value={t} className="bg-sg-navy">{t}</option>)}
            </select>
            <select value={rentBand} onChange={(e) => setRentBand(e.target.value)}
                    data-testid="rent-filter-band"
                    className="h-11 rounded-xl border border-white/25 bg-white/10 backdrop-blur-2xl text-white text-sm px-3 focus:outline-none focus:border-sg-gold appearance-none">
              {RENT_BANDS.map((r) => <option key={r.label} value={r.value} className="bg-sg-navy">{r.label}</option>)}
            </select>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="flex items-baseline justify-between gap-4 mb-6">
          <div>
            <div className="overline text-sg-gold-deep">LIVE INVENTORY</div>
            <h2 className="font-display text-3xl text-sg-navy mt-1">
              {loading ? "Loading rentals…" : `${visibleRentals.length} rental${visibleRentals.length === 1 ? "" : "s"} available`}
            </h2>
          </div>
          <Link to={agent ? `/agent/${agent.slug}/buy` : "/buy"} className="hidden md:inline-flex text-sm text-sg-navy hover:text-sg-gold-deep items-center gap-1">
            Browse buy inventory <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="p-16 text-center text-sg-mute">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Fetching live rentals…
          </div>
        ) : visibleRentals.length === 0 ? (
          <div className="sg-card-cream p-10 text-center">
            <HomeIcon className="w-10 h-10 mx-auto text-sg-gold-deep" />
            <h3 className="font-display text-2xl text-sg-navy mt-4">No live rentals match your filter.</h3>
            <p className="text-sm text-sg-mute mt-2 max-w-md mx-auto">
              {agent
                ? `Tell ${agent.name.split(" ")[0]} what you need on WhatsApp — you'll get vacant-or-soon options within 24 hours.`
                : "WhatsApp our Pune team with your locality, budget and move-in window — we'll route you to a verified agent."}
            </p>
            <a href={waHref} target="_blank" rel="noreferrer"
               className="pill-solid inline-flex items-center gap-2 mt-5"
               data-testid="rent-empty-wa">
              <MessageCircle className="w-4 h-4" /> {agent ? `WhatsApp ${agent.name.split(" ")[0]}` : "Tell us what you need"}
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rent-results-grid">
            {visibleRentals.map((r) => (
              <RentalCard key={r.rental_id} rental={r} detailHref={`${detailBase}/${r.rental_id}`} />
            ))}
          </div>
        )}
      </section>

      {/* WHY */}
      <section className="bg-white border-y border-sg-line py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: <ShieldCheck className="w-5 h-5" />, h: "No data sale", c: "Your number reaches only the listing agent. Never broadcast." },
            { icon: <BadgeCheck className="w-5 h-5" />, h: "MahaCRITI agents", c: "Every rental is posted by a registered agent — reg no. on their portal." },
            { icon: <Building2 className="w-5 h-5" />, h: agent ? `${agent.micro_market} coverage` : "Pune-wide inventory", c: agent ? `${agent.name.split(" ")[0]} transacts in ${agent.primary_locality} and surrounding pockets.` : "Baner · Balewadi · Wakad · Kharadi · Viman Nagar · Wagholi · Magarpatta · Mundhwa." },
          ].map((p) => (
            <div key={p.h} className="sg-card-cream">
              <div className="w-10 h-10 rounded-full bg-white border border-sg-line flex items-center justify-center text-sg-gold-deep">{p.icon}</div>
              <h3 className="font-display text-2xl mt-4 text-sg-navy">{p.h}</h3>
              <p className="text-sm text-sg-mute mt-2">{p.c}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );

  return agent ? inner : <PublicLayout>{inner}</PublicLayout>;
}
